import type { GenEdit, GenQuality, OrderOptions, OutputFormat, SkyStyle, StagingStyle } from '@bkbnlab/api'
import type { LabDescribedLook } from './labStageLooks'
import { Nullable } from 'models/helpers'

/**
 * Option vocabulary of the Photo Studio when it runs on the Lab SDK (FeatureFlag.ALLOW_PHOTO_STUDIO_LAB_SDK).
 * Values are the SDK wire values; the lists drive the panels, the interfaces are what a panel hands to
 * the generate handler. `RoomType` / `FloorFinish` / `WallColor` are not re-exported by `@bkbnlab/api`,
 * hence the structural aliases.
 */
export type LabRoomType = NonNullable<OrderOptions['roomType']>
export type LabFloorFinish = NonNullable<OrderOptions['floorFinish']>
export type LabWallColor = NonNullable<OrderOptions['wallColor']>

/**
 * The ceiling light (Lab `ceiling_light`, indoor_staging 1.20.0): 'style' restages the photographed fitting in the look,
 * 'keep' leaves it. Not in `@bkbnlab/api`'s types yet, hence `LabOrderOptions`.
 */
export type LabCeilingLight = 'keep' | 'style'
export const LAB_CEILING_LIGHTS: LabCeilingLight[] = ['keep', 'style']

/** The SDK order plus what its types do not carry yet */
export type LabOrderOptions = OrderOptions & { ceilingLight?: LabCeilingLight }

/** Analytics discriminator added to every Photo Studio event emitted by the Lab engine. */
export const LAB_SDK_ENGINE = 'lab-sdk'

// ---------------------------------------------------------------------------------------------------
// Enhance
// ---------------------------------------------------------------------------------------------------

export enum LabEnhanceMode {
  BASIC = 'BASIC',
  ADVANCED = 'ADVANCED',
}

export const LAB_SKY_STYLES: SkyStyle[] = ['clear', 'low_cloud', 'neutral', 'high_cloud']

/** Public Tonecraft looks (api/presets.catalog.json). `presets('tonecraft')` may add per-key custom ones. */
export const LAB_TONECRAFT_PROFILES = ['signature', 'natural', 'airy', 'warm'] as const
export type LabTonecraftProfile = typeof LAB_TONECRAFT_PROFILES[number]

/** SAM concepts the privacy blur detects — the wire value is the prompt itself. */
export const LAB_PRIVACY_CATEGORIES = ['human face', 'license plate', 'photo or frame'] as const
export type LabPrivacyCategory = typeof LAB_PRIVACY_CATEGORIES[number]

/**
 * Auto privacy's default set, in Basic and Advanced: faces and plates. Photos & frames stays opt-in (Advanced) —
 * on a staged or enhanced photo it blurs the AI-generated wall art and frames, which protects nothing.
 */
export const LAB_DEFAULT_PRIVACY_CATEGORIES: LabPrivacyCategory[] = ['human face', 'license plate']

/** Translation key segment per category under `lab.privacy` (the wire values contain spaces) — the panel's labels */
export const LAB_PRIVACY_CATEGORY_KEYS: Record<LabPrivacyCategory, string> = {
  'human face': 'human_face',
  'license plate': 'license_plate',
  'photo or frame': 'photo_or_frame',
}

/**
 * The prompts a Lab detection names back, per category: the Lab runs 'photo or frame' as 'framed picture' since
 * lab-back #206 (2026-10-15)
 */
const LAB_PRIVACY_PROMPTS: Record<string, LabPrivacyCategory> = {
  'human face': 'human face',
  'license plate': 'license plate',
  'photo or frame': 'photo or frame',
  'framed picture': 'photo or frame',
}

/** The category a Lab detection's prompt belongs to; null for a prompt the Studio does not ask for */
export const resolveLabPrivacyCategory = (prompt?: Nullable<string>): LabPrivacyCategory | null =>
  (prompt && LAB_PRIVACY_PROMPTS[prompt.trim().toLowerCase()]) || null

export const LAB_IMAGE_FORMATS: OutputFormat[] = ['jpeg', 'png', 'webp']

export const LAB_QUALITY_MIN = 40
export const LAB_QUALITY_MAX = 100
export const LAB_QUALITY_DEFAULT = 92

export interface LabEnhanceSettings {
  mode: LabEnhanceMode
  perspective: boolean
  /** 0..1 — the SDK default is 1 */
  perspectiveStrength: number
  tonecraft: boolean
  tonecraftProfile: LabTonecraftProfile
  autoLevels: boolean
  privacy: boolean
  privacyCategories: LabPrivacyCategory[]
  replaceSky: boolean
  skyStyle: SkyStyle
  imageFormat: boolean
  format: OutputFormat
  quality: number
}

/**
 * Basic mode runs these: Perspective on, Tonecraft Signature + Auto levels, Auto privacy on (faces and plates).
 * Replace sky is the one user choice Basic keeps; it starts on Clear.
 */
export const DEFAULT_LAB_ENHANCE_SETTINGS: LabEnhanceSettings = {
  mode: LabEnhanceMode.BASIC,
  perspective: true,
  perspectiveStrength: 1,
  tonecraft: true,
  tonecraftProfile: 'signature',
  autoLevels: true,
  privacy: true,
  privacyCategories: [...LAB_DEFAULT_PRIVACY_CATEGORIES],
  replaceSky: false,
  skyStyle: 'clear',
  imageFormat: false,
  format: 'jpeg',
  quality: LAB_QUALITY_DEFAULT,
}

// ---------------------------------------------------------------------------------------------------
// Stage
// ---------------------------------------------------------------------------------------------------

/** The four "Furnitures" cards. Values are the SDK `genEdit` keys. */
export type LabStageMode = Extract<GenEdit, 'indoor_staging' | 'declutter_local' | 'mess_cleaner' | 'outdoor_staging'>
export const LAB_STAGE_MODES: LabStageMode[] = ['indoor_staging', 'declutter_local', 'mess_cleaner', 'outdoor_staging']

/** Speed (genQuality) applies to Stage a room and Stage a garden or terrace only — product decision. */
export const LAB_STAGE_MODES_WITH_SPEED: LabStageMode[] = ['indoor_staging', 'outdoor_staging']

/**
 * A Room style tile: a Lab look (Ikea left out), or one of Photo Studio's own looks the Lab runs as a described style
 * (`labStageLooks.ts`).
 */
export type LabStageLook = Exclude<StagingStyle, 'ikea'> | LabDescribedLook

export const LAB_STAGING_STYLES: LabStageLook[] = [
  'keep', 'contemporary', 'modern', 'scandinavian', 'luxury', 'industrial', 'coastal', 'mountain',
  'office_space', 'warehouse', 'retail_store', 'gym', 'restaurant', 'dental_practice',
]
export const LAB_ROOM_TYPES: LabRoomType[] = ['auto', 'living_room', 'dining_room', 'bedroom', 'child_room', 'kitchen', 'bathroom', 'office', 'terrace']
export const LAB_FLOOR_FINISHES: LabFloorFinish[] = ['keep', 'hardwood_light_brown', 'hardwood_aria', 'tiles_white', 'tiles_grey', 'concrete_light_grey', 'carpet_grey', 'carpet_dark_blue']
export const LAB_WALL_COLORS: LabWallColor[] = ['keep', 'white', 'warm_white', 'cream', 'greige', 'light_grey', 'sand', 'taupe', 'sage_green', 'olive', 'powder_blue', 'terracotta', 'charcoal']
export const LAB_SPEEDS: GenQuality[] = ['fast', 'balanced', 'best']

/** Swatch colours for the Walls / Floor selects — same values as the Playground (staging.js). */
export const LAB_FINISH_SWATCHES: Partial<Record<LabFloorFinish | LabWallColor, string>> = {
  carpet_dark_blue: '#2F3B5C',
  carpet_grey: '#8B8E93',
  concrete_light_grey: '#C8CACC',
  hardwood_aria: '#DDAF77',
  hardwood_light_brown: '#B98A5C',
  tiles_grey: '#ADB1B4',
  tiles_white: '#ECECEA',
  white: '#F7F7F5',
  warm_white: '#F4EFE6',
  cream: '#F1E8D7',
  greige: '#D8D2C6',
  light_grey: '#D9DBDD',
  sand: '#E4D6BF',
  taupe: '#B9AA9B',
  sage_green: '#B9C4B0',
  olive: '#9FA583',
  powder_blue: '#BDCEDA',
  terracotta: '#C97D5D',
  charcoal: '#40454A',
}

/** Rough run times per Quality tier, display only. Best: ~60 s, product decision 2026-10-13 (was 90). */
export const LAB_SPEED_ESTIMATE_SECONDS: Record<GenQuality, number> = { fast: 27, balanced: 44, best: 60 }

/** Lab's limit on a described style (`styleClause`) */
export const LAB_STYLE_CLAUSE_MAX = 1500

/** The Room style tile that describes the style in the user's own words — a mode next to the looks, not a look */
export const LAB_CUSTOM_STYLE = 'custom'

export interface LabStageSettings {
  mode: LabStageMode
  /** The look; with `customStyle` on, the one whose style text the description replaces */
  style: LabStageLook
  /** "Describe" is on: `styleClause` replaces the look's style text (Stage a room only) */
  customStyle: boolean
  /** The style in the user's own words — blank leaves it to the look */
  styleClause: string
  roomType: LabRoomType
  floorFinish: LabFloorFinish
  wallColor: LabWallColor
  /** Restaged in the look by default: the photographed light rarely fits the new room. Ignored with "Keep as is". */
  ceilingLight: LabCeilingLight
  speed: GenQuality
}

/** "Keep as is" by default — the user picks a look, or only a wall / floor finish. */
export const DEFAULT_LAB_STAGE_SETTINGS: LabStageSettings = {
  mode: 'indoor_staging',
  style: 'keep',
  customStyle: false,
  styleClause: '',
  roomType: 'auto',
  floorFinish: 'keep',
  wallColor: 'keep',
  ceilingLight: 'style',
  speed: 'balanced',
}

// ---------------------------------------------------------------------------------------------------
// Progress
// ---------------------------------------------------------------------------------------------------

/**
 * Client-side stages emitted around the SDK's own order stages (edit, sky, detect, gen, finish…). Everything before
 * the order runs — PREPARE (snapshot, picking the source), UPLOAD (the photo, when not already on the Lab),
 * UPLOAD_MASK (the eraser's selection), QUEUED (order submitted, waiting for a worker) — reads as the plan's leading
 * "Prepare" step, each with its own message.
 */
export enum LabLocalStage {
  PREPARE = 'prepare',
  UPLOAD = 'upload',
  UPLOAD_MASK = 'upload_mask',
  QUEUED = 'queued',
  EXPORT = 'export',
}

/** What an erase run records with its result (analytics): the brush and how many marks were drawn */
export interface LabEraseSettings {
  brushSize: number
  numberOfMasks: number
}

// ---------------------------------------------------------------------------------------------------
// All features at once
// ---------------------------------------------------------------------------------------------------

/**
 * One run of the "Display all features at once" view: the Enhance operations plus at most one generative edit —
 * Stage, or the eraser (whose mask is uploaded with the run). The Lab runs one gen edit per order, as in the Playground.
 */
export interface LabCombinedSettings {
  enhance?: LabEnhanceSettings
  generative?: { kind: 'stage', settings: LabStageSettings } | { kind: 'erase' }
}

/**
 * A step of the running order to stage over the photo (the socket's scenes, as the Lab playground stages them): an edit
 * or a generation brings the image the next steps work on; a detection, what it found on that image.
 */
export type LabSceneContent =
  | {
    kind: 'edit' | 'generate'
    /** The step's image (a presigned URL) */
    image: string
    /** What an edit did (`perspective`, …) */
    steps: string[]
  }
  | {
    kind: 'detect'
    /**
     * What was looked for: the category of its prompt, or — a detection of several prompts names none — the categories
     * the run asked for. Empty when neither is known.
     */
    categories: LabPrivacyCategory[]
    /** Share of the image the mask covers, in percent */
    coverage?: Nullable<number>
    /** The mask, white on black: a PNG data URL */
    mask: string
  }

/** A scene of the run; `id` orders them */
export type LabScene = LabSceneContent & { id: number }

export interface LabProgress {
  /**
   * Plan stage key (edit, sky, detect, room, fal, gen, finish) once resolved by the controller; before
   * that, the SDK's free-form `progressStage` (`sampling 5/8`, `fal-1`…) or a LabLocalStage.
   */
  stage: string
  /** The SDK label as received, kept for logging / debugging */
  rawStage?: string
  /** 0..100 */
  percent: number
  etaMs?: number
  /** The running stage's clock, when the order's stage list is known — lets the overlay advance between real events */
  clock?: LabStageClock
}

/**
 * A running Lab order stage on its typical duration (see `labStageClock.ts`). A cloud generation holds the same
 * percentage until it is done, so the overlay eases from `base` towards `ceil` on this clock in the meantime.
 */
export interface LabStageClock {
  /** The order's stage name (`edit`, `fal`, `gen`, `finish`, …) */
  name: string
  /** Overall percentage at which the stage starts and ends */
  start: number
  ceil: number
  /** Where the eased curve starts: the stage start, raised by real progress reported within the stage */
  base: number
  /** Typical duration of the stage */
  estMs: number
  /** Local time the stage was first reported */
  startedAt: number
}
